import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import DashboardLayout from "../dashboard/_components/DashboardLayout";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import {
  Users,
  Search,
  Download,
  Eye,
  Mail,
  Phone,
  Calendar,
  RefreshCw,
  Settings,
  User,
  X,
} from "lucide-react";

export default function Customers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Column visibility state based on Firebase schema
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    email: true,
    phone: true,
    uid: true,
    createdAt: true,
    updatedAt: true,
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchCustomers = async () => {
    setLoading(true);

    try {
      const usersRef = collection(db, "users");

      const q = query(usersRef, orderBy("createdAt", "desc"));

      const querySnapshot = await getDocs(q);

      const customersData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        customersData.push({
          id: doc.id,
          ...data,
        });
      });
      setCustomers(customersData);
    } catch (error) {
      // Silent fail - try fallback

      // If createdAt doesn't exist, try without ordering
      try {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);

        const customersData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          customersData.push({
            id: doc.id,
            ...data,
          });
        });

        // Sort by createdAt desc (newest first) if available
        customersData.sort((a, b) => {
          const timeA = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
          const timeB = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
          return timeB - timeA;
        });

        setCustomers(customersData);
      } catch (err) {
        // Silent fail
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCustomers();
    }
  }, [user]);

  // Filter and search customers
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phoneNumber?.includes(searchTerm) ||
      customer.uid?.includes(searchTerm);

    return matchesSearch;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      }
      return new Date(timestamp).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleExport = () => {
    // Export to CSV based on visible columns
    const headers = [];
    const columnMap = [];

    if (visibleColumns.name) {
      headers.push("Full Name");
      columnMap.push("fullName");
    }
    if (visibleColumns.email) {
      headers.push("Email");
      columnMap.push("email");
    }
    if (visibleColumns.phone) {
      headers.push("Phone Number");
      columnMap.push("phoneNumber");
    }
    if (visibleColumns.uid) {
      headers.push("User ID");
      columnMap.push("uid");
    }
    if (visibleColumns.createdAt) {
      headers.push("Created At");
      columnMap.push("createdAt");
    }
    if (visibleColumns.updatedAt) {
      headers.push("Updated At");
      columnMap.push("updatedAt");
    }

    const csvData = filteredCustomers.map((c) => {
      const row = [];
      columnMap.forEach((col) => {
        if (col === "createdAt" || col === "updatedAt") {
          row.push(formatDate(c[col]));
        } else {
          row.push(c[col] || "");
        }
      });
      return row;
    });

    const csv = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      {/* Header Section */}
      <div className="mb-4 sm:mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <div className="p-1.5 bg-blue-600 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              Customers
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              Manage and view all customer information
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline text-xs font-medium">
                  Columns
                </span>
              </button>

              {/* Column Settings Dropdown */}
              {showColumnSettings && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        Show Columns
                      </h3>
                      <button
                        onClick={() => setShowColumnSettings(false)}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={visibleColumns.name}
                          onChange={() => toggleColumn("name")}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                          disabled
                        />
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">Name</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={visibleColumns.email}
                          onChange={() => toggleColumn("email")}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">Email</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={visibleColumns.phone}
                          onChange={() => toggleColumn("phone")}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          Phone Number
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={visibleColumns.uid}
                          onChange={() => toggleColumn("uid")}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">User ID</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={visibleColumns.createdAt}
                          onChange={() => toggleColumn("createdAt")}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          Created At
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                        <input
                          type="checkbox"
                          checked={visibleColumns.updatedAt}
                          onChange={() => toggleColumn("updatedAt")}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                        />
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          Updated At
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={fetchCustomers}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline text-xs font-medium">
                Refresh
              </span>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline text-xs font-medium">
                Export
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs font-medium mb-0.5">
                Total Customers
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                {customers.length}
              </h3>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs font-medium mb-0.5">
                With Email
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                {
                  customers.filter((c) => c.email && c.email.trim() !== "")
                    .length
                }
              </h3>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs font-medium mb-0.5">
                New This Month
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                {
                  customers.filter((c) => {
                    if (!c.createdAt) return false;
                    const date = c.createdAt.toDate
                      ? c.createdAt.toDate()
                      : new Date(c.createdAt);
                    return date.getMonth() === new Date().getMonth();
                  }).length
                }
              </h3>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="rounded-xl shadow-sm mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, email, phone number, or user ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading customers...</p>
              </div>
            </div>
          ) : currentCustomers.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg font-medium">
                  No customers found
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm
                    ? "Try adjusting your search terms"
                    : "Customers will appear here when added"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    {visibleColumns.name && (
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Customer Name
                      </th>
                    )}
                    {visibleColumns.email && (
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Email
                      </th>
                    )}
                    {visibleColumns.phone && (
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Phone Number
                      </th>
                    )}
                    {visibleColumns.uid && (
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        User ID
                      </th>
                    )}
                    {visibleColumns.createdAt && (
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Created At
                      </th>
                    )}
                    {visibleColumns.updatedAt && (
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Updated At
                      </th>
                    )}
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {currentCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      {visibleColumns.name && (
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                              <span className="text-white font-semibold text-sm">
                                {customer.fullName && customer.fullName.trim() !== ""
                                  ? customer.fullName.charAt(0).toUpperCase()
                                  : "U"}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <Link
                                to={`/customers/${customer.id}`}
                                className="font-semibold text-sm text-gray-900 hover:text-blue-600 transition-colors block"
                              >
                                {customer.fullName && customer.fullName.trim() !== ""
                                  ? customer.fullName
                                  : "Unnamed User"}
                              </Link>
                              <div className="text-xs text-gray-500 truncate">
                                ID: {customer.id.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      {visibleColumns.email && (
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600 truncate max-w-[200px]">
                              {customer.email && customer.email.trim() !== ""
                                ? customer.email
                                : "-"}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.phone && (
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600">
                              {customer.phoneNumber || "-"}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.uid && (
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400 shrink-0" />
                            <code className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200 truncate max-w-[150px] block">
                              {customer.uid || "-"}
                            </code>
                          </div>
                        </td>
                      )}
                      {visibleColumns.createdAt && (
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600">
                              {formatDate(customer.createdAt)}
                            </span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.updatedAt && (
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600">
                              {formatDate(customer.updatedAt)}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="px-4 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() =>
                              navigate(`/customers/${customer.id}`)
                            }
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Customer Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 sm:px-6 py-4 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-gray-600">
                      Showing {indexOfFirstItem + 1} to{" "}
                      {Math.min(indexOfLastItem, filteredCustomers.length)} of{" "}
                      {filteredCustomers.length} customers
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <div className="flex items-center gap-1">
                        {[...Array(totalPages)].map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === i + 1
                                ? "bg-blue-600 text-white"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(totalPages, prev + 1)
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
